import { useRef } from 'react';

// ── Design tokens (JS version) — Tactile Archive palette ───────────────────────
export const C = {
  bg:'#faf9f8', bg2:'#f4f3f2', bg3:'#e9e8e7',
  surface:'#ffffff', surface2:'#f4f3f2',
  border:'#e5e3e0', border2:'#c5c5d4',
  text:'#1a1c1c', text2:'#454652', text3:'#6a6b76',
  indigo:'#24389c', indigoDark:'#1c2c7a', indigoLight:'#7f8fe0',
  violet:'#6b3fa0', emerald:'#2f6b3a', amber:'#a06014',
  rose:'#8a2828', sky:'#24389c', teal:'#1a7a6a',
};

// ── Style helpers ─────────────────────────────────────────────────────────────
export const S = {
  card: {
    background: C.surface, border: `1px solid ${C.border}`,
    borderRadius: 12, padding: 18,
  },
  input: {
    width: '100%', padding: '9px 12px',
    background: C.bg, border: `1px solid ${C.border2}`,
    borderRadius: 8, color: C.text,
    fontFamily: "'Manrope', sans-serif", fontSize: 13,
    outline: 'none', boxSizing: 'border-box',
  },
  label: {
    display: 'block', fontSize: 11, fontWeight: 600,
    color: C.text3, marginBottom: 5,
    textTransform: 'uppercase', letterSpacing: '.05em',
  },
  btn: (color = C.indigo) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: color, color: '#fff',
    fontFamily: "'Manrope', sans-serif", fontSize: 13, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  }),
  btnGhost: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 8,
    background: 'transparent', color: C.text2,
    border: `1px solid ${C.border2}`,
    fontFamily: "'Manrope', sans-serif", fontSize: 13,
    cursor: 'pointer',
  },
};

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ title, subtitle, color = C.indigo, children, right, style = {}, accent }) {
  return (
    <div style={{
      ...S.card, ...style,
      borderTop: accent !== false ? `2px solid ${color}` : 'none',
      transition: 'border-color .2s',
    }}>
      {(title || right) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color, letterSpacing: '.01em' }}>{title}</h4>
            {subtitle && <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>{subtitle}</div>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
export function StatCard({ label, value, color = C.indigo, icon, trend }) {
  return (
    <div style={{ ...S.card, textAlign: 'center', borderTop: `2px solid ${color}` }}>
      {icon && <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>}
      <div style={{ fontSize: 11, color: C.text3, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
      {trend && <div style={{ fontSize: 10, color: trend > 0 ? C.emerald : C.rose, marginTop: 4 }}>{trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%</div>}
    </div>
  );
}

// ── Button ────────────────────────────────────────────────────────────────────
export function Btn({ children, onClick, color = C.indigo, ghost, danger, sm, xs, disabled, style = {}, icon, loading }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    borderRadius: sm || xs ? 6 : 8, border: 'none',
    fontFamily: "'Manrope', sans-serif", fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? .5 : 1,
    transition: 'all .18s',
    whiteSpace: 'nowrap',
    ...style,
  };
  const sizes = xs ? { padding: '3px 8px', fontSize: 10 } : sm ? { padding: '5px 11px', fontSize: 11 } : { padding: '8px 16px', fontSize: 13 };
  const variant = ghost
    ? { background: 'transparent', color: C.text2, border: `1px solid ${C.border2}` }
    : danger
    ? { background: 'rgba(244,63,94,.12)', color: '#f43f5e', border: '1px solid rgba(244,63,94,.2)' }
    : { background: color, color: '#fff' };

  return (
    <button onClick={onClick} disabled={disabled} style={{ ...base, ...sizes, ...variant }}>
      {loading ? <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite', display: 'inline-block' }}/> : icon}
      {children}
    </button>
  );
}

// ── Input ─────────────────────────────────────────────────────────────────────
export function Input({ label, value, onChange, placeholder, type = 'text', style = {}, autoFocus }) {
  return (
    <div>
      {label && <label style={S.label}>{label}</label>}
      <input
        type={type} value={value} onChange={onChange} placeholder={placeholder}
        autoFocus={autoFocus}
        style={{ ...S.input, ...style }}
        onFocus={e => { e.target.style.borderColor = C.indigo; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,.15)'; }}
        onBlur={e  => { e.target.style.borderColor = C.border2; e.target.style.boxShadow = 'none'; }}
      />
    </div>
  );
}

// ── Textarea ──────────────────────────────────────────────────────────────────
export function Textarea({ label, value, onChange, placeholder, rows = 4, style = {} }) {
  return (
    <div>
      {label && <label style={S.label}>{label}</label>}
      <textarea
        value={value} onChange={onChange} placeholder={placeholder} rows={rows}
        style={{ ...S.input, resize: 'vertical', lineHeight: 1.6, ...style }}
        onFocus={e => { e.target.style.borderColor = C.indigo; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,.15)'; }}
        onBlur={e  => { e.target.style.borderColor = C.border2; e.target.style.boxShadow = 'none'; }}
      />
    </div>
  );
}

// ── Select ────────────────────────────────────────────────────────────────────
export function Select({ label, value, onChange, options, style = {} }) {
  return (
    <div>
      {label && <label style={S.label}>{label}</label>}
      <select value={value} onChange={onChange}
        style={{ ...S.input, cursor: 'pointer', ...style }}
        onFocus={e => { e.target.style.borderColor = C.indigo; }}
        onBlur={e  => { e.target.style.borderColor = C.border2; }}>
        {options.map(o => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, maxWidth = 560 }) {
  if (!open) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,.7)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      padding: 16, backdropFilter: 'blur(4px)',
      animation: 'fadeIn .2s ease',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 16, padding: 28, width: '100%', maxWidth,
        boxShadow: '0 24px 80px rgba(26,28,28,.25)',
        animation: 'fadeUp .25s ease',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        {title && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{title}</h3>
            <button onClick={onClose} style={{ background: C.bg2, border: 'none', color: C.text2, borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 14 }}>✕</button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

// ── Toast / Alert ─────────────────────────────────────────────────────────────
export function Toast({ message, onClose }) {
  if (!message) return null;
  const isSuccess = message.startsWith('✅') || message.startsWith('🎉');
  const isError   = message.startsWith('❌') || message.startsWith('⚠️');
  const bg = isSuccess ? C.emerald : isError ? C.rose : C.indigo;
  return (
    <div style={{
      position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)',
      background: bg, color: '#fff', padding: '11px 20px', borderRadius: 10,
      zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,.4)',
      display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 500,
      maxWidth: 440, animation: 'fadeUp .25s ease',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'rgba(255,255,255,.2)', border: 'none', color: '#fff', borderRadius: 4, padding: '2px 7px', cursor: 'pointer' }}>✕</button>
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────
export function Badge({ children, color = C.indigo }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '2px 8px', borderRadius: 20,
      background: color + '20', color, border: `1px solid ${color}40`,
      fontSize: 10, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
    }}>{children}</span>
  );
}

// ── Progress ──────────────────────────────────────────────────────────────────
export function Progress({ value, color = C.indigo, height = 6 }) {
  return (
    <div style={{ background: C.bg3, borderRadius: 99, height, overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(100, value)}%`, background: color, height: '100%', borderRadius: 99, transition: 'width .6s cubic-bezier(.4,0,.2,1)' }}/>
    </div>
  );
}

// ── Avatar ────────────────────────────────────────────────────────────────────
export function Avatar({ src, name, size = 36, style = {} }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  if (src) return <img src={src} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', ...style }}/>;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * .38, fontWeight: 700, color: '#fff', flexShrink: 0, ...style,
    }}>{initials}</div>
  );
}

// ── FileUploadButton ───────────────────────────────────────────────────────────
export function FileUploadBtn({ onFile, accept = '*', children, ...btnProps }) {
  const ref = useRef(null);
  return (
    <>
      <Btn onClick={() => ref.current?.click()} {...btnProps}>{children}</Btn>
      <input ref={ref} type="file" accept={accept} style={{ display: 'none' }}
        onChange={e => { const f = e.target.files[0]; if (f) { onFile(f); e.target.value = ''; } }}/>
    </>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
export function Skeleton({ height = 20, width = '100%', radius = 6, style = {} }) {
  return <div style={{ height, width, borderRadius: radius, ...style }} className="skeleton"/>;
}

// ── Empty state ───────────────────────────────────────────────────────────────
export function Empty({ icon = '📭', title, message, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: C.text3 }}>
      <div style={{ fontSize: 44, marginBottom: 12 }}>{icon}</div>
      {title   && <div style={{ fontSize: 15, fontWeight: 600, color: C.text2, marginBottom: 6 }}>{title}</div>}
      {message && <div style={{ fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>{message}</div>}
      {action}
    </div>
  );
}
